import { Task } from "@/api.types";
import { ZOOM_LEVELS } from "@/components/markers/marker.util";
import { AdvancedMarker } from "@vis.gl/react-google-maps";
import { taskSearchQueries, useTasks } from "@/api/tasks";
import { useEffect, useCallback, useState, useMemo } from "react";
import { useOrderSearchStore } from "@/features/orders/order-search-store";
import { useShortcutsStore } from "@/screens/dashboard/shortcuts.store";

interface TaskGroupMarkerComponentProps {
  tasks: Task[];
  zoomLevel: number;
  isHighlighted?: boolean;
}

function TaskGroupMarkerComponent({
  tasks,
  zoomLevel,
  isHighlighted = false,
}: TaskGroupMarkerComponentProps) {
  // Add state for tooltip expansion
  const [isTooltipExpanded, setIsTooltipExpanded] = useState(false);

  const getScale = () => {
    // Increase scale for highlighted items
    if (isHighlighted) {
      if (zoomLevel < ZOOM_LEVELS.MEDIUM) return 0.65;
      if (zoomLevel < ZOOM_LEVELS.LARGE) return 0.9;
      return 1.2;
    }

    // Normal scale
    if (zoomLevel < ZOOM_LEVELS.MEDIUM) return 0.5;
    if (zoomLevel < ZOOM_LEVELS.LARGE) return 0.75;
    return 1;
  };

  const pickupCount = tasks.filter((task) => task.type === "PICKUP").length;
  const deliveryCount = tasks.filter((task) => task.type === "DELIVERY").length;

  // Find all failed parent tasks and group them by order and failure message
  const failedParentTasksByOrder = tasks
    .map((task) => task.parentTask)
    .filter(
      (parentTask): parentTask is Task =>
        parentTask !== null &&
        parentTask !== undefined &&
        (parentTask.status === "FAILED" || parentTask.status === "CANCELLED")
    )
    .reduce(
      (acc, task) => {
        const orderNumber = task.order?.orderNumber;
        const failureMessage = task.failureReason ?? "Failed to complete task";
        const status = task.status;
        const taskType = task.type; // Type of the parent task

        if (!orderNumber || !taskType) return acc; // Ensure we have order number and type

        // Key groups by order, message, status
        const key = `${orderNumber}-${failureMessage}-${status}`;

        if (!acc[key]) {
          acc[key] = {
            orderNumber,
            status,
            failureMessage,
            countsByType: {}, // Store counts per type (PICKUP/DELIVERY)
            totalCount: 0,
          };
        }

        // Increment count for this specific type
        acc[key].countsByType[taskType] =
          (acc[key].countsByType[taskType] || 0) + 1;
        acc[key].totalCount++;

        return acc;
      },
      {} as Record<
        string,
        {
          orderNumber: string;
          status: string;
          failureMessage: string;
          countsByType: Record<string, number>; // e.g., { PICKUP: 1, DELIVERY: 1 }
          totalCount: number;
        }
      >
    );

  // Get the entries to display - used for conditional rendering
  const failedOrderEntries = Object.values(failedParentTasksByOrder);
  const totalFailedGroups = failedOrderEntries.length;

  // Determine content based on expansion state
  const displayedEntries = isTooltipExpanded
    ? failedOrderEntries
    : failedOrderEntries.slice(0, 1); // Show only the first item when collapsed

  // Create tooltip content JSX based on displayedEntries
  const tooltipOrderList = displayedEntries.length > 0 && (
    <div className="flex flex-col divide-y divide-white/20">
      {displayedEntries.map((group) => (
        <div
          key={`${group.orderNumber}-${group.failureMessage}-${group.status}`}
          className="py-2 first:pt-0 last:pb-0"
        >
          {/* Info Section */}
          <div className="flex-grow">
            {/* Order Number and Status Row */}
            <div className="flex items-center flex-wrap gap-2 mb-1">
              <div className="font-medium">Order #{group.orderNumber}</div>
              <div
                className={`
                  text-xs px-2 py-0.5 rounded-full font-medium
                  ${
                    group.status === "FAILED"
                      ? "bg-white/20 text-red-100"
                      : "bg-white/20 text-orange-100"
                  }
                `}
              >
                {group.status === "FAILED" ? "Failed" : "Cancelled"}
                {group.totalCount > 1 ? ` (${group.totalCount})` : ""}
              </div>
            </div>

            {/* Failure Message Row */}
            <div className="text-xs text-white/90 flex items-start gap-2">
              <svg
                className="w-3 h-3 mt-0.5 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>{group.failureMessage}</span>
            </div>

            {/* Buttons Row - Conditionally render based on expansion state */}
            {isTooltipExpanded && (
              <div className="flex flex-wrap gap-2 mt-2">
                {(group.countsByType.PICKUP ?? 0) > 0 && (
                  <button
                    className="text-xs bg-red-700 hover:bg-red-600 text-white px-2 py-1 rounded"
                    title={`Cancel Pickup for Order ${group.orderNumber}`}
                  >
                    Cancel Pickup
                    {(group.countsByType.PICKUP ?? 0) > 1
                      ? ` (${group.countsByType.PICKUP})`
                      : ""}
                  </button>
                )}
                {(group.countsByType.DELIVERY ?? 0) > 0 && (
                  <>
                    <button
                      className="text-xs bg-red-700 hover:bg-red-600 text-white px-2 py-1 rounded"
                      title={`Cancel Delivery for Order ${group.orderNumber}`}
                    >
                      Cancel Delivery
                      {(group.countsByType.DELIVERY ?? 0) > 1
                        ? ` (${group.countsByType.DELIVERY})`
                        : ""}
                    </button>
                    <button
                      className="text-xs bg-yellow-700 hover:bg-yellow-600 text-white px-2 py-1 rounded"
                      title={`Return Order ${group.orderNumber}`}
                    >
                      Return Order
                      {(group.countsByType.DELIVERY ?? 0) > 1
                        ? ` (${group.countsByType.DELIVERY})`
                        : ""}
                    </button>
                    <button
                      className="text-xs bg-blue-700 hover:bg-blue-600 text-white px-2 py-1 rounded"
                      title={`Create New Order & Return ${group.orderNumber}`}
                    >
                      New + Return
                      {(group.countsByType.DELIVERY ?? 0) > 1
                        ? ` (${group.countsByType.DELIVERY})`
                        : ""}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div
      className={`
        relative cursor-pointer 
        ${isHighlighted ? "animate-pulse" : ""}
      `}
      style={{
        transform: `scale(${getScale()})`,
        transformOrigin: "bottom center",
      }}
    >
      {/* Message Indicator */}
      {Object.keys(failedParentTasksByOrder).length > 0 && (
        <>
          {zoomLevel >= ZOOM_LEVELS.MEDIUM ? (
            // Full tooltip for medium and larger zoom levels
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-10">
              {" "}
              {/* Added z-index */}
              <div className="relative bg-red-900/95 backdrop-blur-sm rounded-lg shadow-lg p-3 text-sm min-w-[300px] text-white">
                {/* Add Expand/Collapse Icon Button */}
                {totalFailedGroups >= 1 && ( // Only show toggle if more than one group
                  <button
                    onClick={() => setIsTooltipExpanded(!isTooltipExpanded)}
                    className="absolute top-2 right-2 p-1 text-white/70 hover:text-white focus:outline-none z-20" // Added z-index
                    aria-label={
                      isTooltipExpanded ? "Collapse tooltip" : "Expand tooltip"
                    }
                  >
                    {isTooltipExpanded ? (
                      // Collapse Icon (Chevron Up)
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 15l7-7 7 7"
                        ></path>
                      </svg>
                    ) : (
                      // Expand Icon (Chevron Down)
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        ></path>
                      </svg>
                    )}
                  </button>
                )}

                {/* Render the list of orders */}
                {tooltipOrderList}

                {/* REMOVED Show More/Less Button */}
              </div>
              {/* Triangle pointer */}
              <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-red-900/95 transform rotate-45"></div>
            </div>
          ) : (
            // Simple indicator for small zoom levels
            <div className="absolute -top-1 -right-1">
              <div className="relative">
                <div className="absolute -inset-1 bg-red-500 rounded-full animate-ping opacity-75"></div>
                <div className="relative h-2 w-2 bg-red-600 rounded-full"></div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Search highlight indicator */}
      {isHighlighted && (
        <>
          <div className="absolute -inset-1.5 rounded-lg bg-yellow-400/30 animate-pulse"></div>
          <div
            className="absolute -inset-3 rounded-full border-2 border-yellow-400/70 border-dashed animate-spin"
            style={{ animationDuration: "8s" }}
          ></div>
        </>
      )}

      <div
        className={`
        flex flex-row items-stretch h-10 overflow-hidden rounded-lg transition-transform duration-200
        ${isHighlighted ? "ring-4 ring-yellow-400 shadow-lg" : "hover:scale-110"}
      `}
      >
        {pickupCount > 0 && (
          <div
            className={`w-10 ${isHighlighted ? "bg-blue-500 font-bold" : "bg-blue-600"} flex items-center justify-center`}
          >
            <span className="text-white text-sm font-medium">
              {pickupCount}
            </span>
          </div>
        )}
        {deliveryCount > 0 && (
          <div
            className={`w-10 ${isHighlighted ? "bg-purple-300 font-bold" : "bg-purple-400"} flex items-center justify-center`}
          >
            <span className="text-white text-sm font-medium">
              {deliveryCount}
            </span>
          </div>
        )}
      </div>
      {/* Connector line and dot */}
      <div className="flex flex-col items-center pt-0.5">
        <div className="w-0.5 h-2 bg-black"></div> {/* Connector Line */}
        <div className="w-1.5 h-1.5 bg-white rounded-full border-[1px] border-black"></div>{" "}
        {/* Connector Dot */}
      </div>
    </div>
  );
}

interface TaskGroupMarkersProps {
  zoomLevel: number;
  selectedTasks: Task[];
}

export function TaskGroupMarkers({ zoomLevel }: TaskGroupMarkersProps) {
  const { showPickups, showDeliveries } = useShortcutsStore();

  // Use the global order search store with alternative approach
  const store = useOrderSearchStore();
  const searchedOrderNumber = store.searchedOrderNumber;

  // Type assertion to avoid TypeScript errors
  const setMatchCount = (count: number) => {
    (store.setMatchCount as (count: number) => void)(count);
  };

  // Use separate query hooks for pickups and deliveries
  const { data: pickupTasksResponse } = useTasks(
    taskSearchQueries.activePickupTasks
  );

  const { data: deliveryTasksResponse } = useTasks(
    taskSearchQueries.activeDeliveryTasks
  );

  // Get assigned tasks to include in search
  const { data: assignedTasksResponse } = useTasks(
    searchedOrderNumber ? taskSearchQueries.activeTasks : { limit: 0 }
  );

  // Memoize tasks to prevent recreation on each render
  const pickupTasks = useMemo(
    () => (showPickups ? (pickupTasksResponse?.data ?? []) : []),
    [pickupTasksResponse?.data, showPickups]
  );

  const deliveryTasks = useMemo(
    () => (showDeliveries ? (deliveryTasksResponse?.data ?? []) : []),
    [deliveryTasksResponse?.data, showDeliveries]
  );

  // Combine with assigned tasks when searching
  const assignedTasks = useMemo(
    () => (searchedOrderNumber ? (assignedTasksResponse?.data ?? []) : []),
    [assignedTasksResponse?.data, searchedOrderNumber]
  );

  // Define taskMatchesSearch function as a callback
  const taskMatchesSearch = useCallback(
    (task: Task): boolean => {
      if (!searchedOrderNumber) return false;

      // Normalize the search string - remove "#" and trim
      const normalizedSearch = searchedOrderNumber.replace(/^#/, "").trim();

      // Helper function to normalize and check order number
      const checkOrderNumber = (
        orderNum: string | number | undefined
      ): boolean => {
        if (!orderNum) return false;

        const stringOrderNum = String(orderNum).trim();

        // Try to extract just the number part
        const extractedNumber = stringOrderNum
          .replace(/^(Order\s*)?(#)?/, "")
          .trim();
        return extractedNumber === normalizedSearch;
      };

      // Check task order
      if (checkOrderNumber(task.order?.orderNumber)) {
        return true;
      }

      // Check parent task order
      if (checkOrderNumber(task.parentTask?.order?.orderNumber)) {
        return true;
      }

      return false;
    },
    [searchedOrderNumber]
  );

  // Process tasks and update match count when needed
  useEffect(() => {
    if (!searchedOrderNumber) return;

    const taskFinalStatuses = ["COMPLETED", "CANCELLED", "FAILED"];

    // Process pickups, deliveries, and assigned tasks separately
    const filteredPickups = showPickups
      ? pickupTasks.filter(
          (task) => !task.tripId && !taskFinalStatuses.includes(task.status)
        )
      : [];

    const filteredDeliveries = showDeliveries
      ? deliveryTasks.filter(
          (task) => !task.tripId && !taskFinalStatuses.includes(task.status)
        )
      : [];

    // Combine filtered tasks
    const allFilteredTasks = [
      ...filteredPickups,
      ...filteredDeliveries,
      ...assignedTasks,
    ];

    // Count matches
    const matchingTasks = allFilteredTasks.filter(taskMatchesSearch);
    const matchCount = matchingTasks.length;

    // Report matches count
    setMatchCount(matchCount);
  }, [
    pickupTasks,
    deliveryTasks,
    assignedTasks,
    searchedOrderNumber,
    showPickups,
    showDeliveries,
    taskMatchesSearch,
  ]);

  // If no tasks data yet, return nothing
  if (!pickupTasks && !deliveryTasks) return null;

  // Apply client-side filtering for active tasks
  const taskFinalStatuses = ["COMPLETED", "CANCELLED", "FAILED"];

  // Process pickups and deliveries separately
  const filteredPickups = showPickups
    ? pickupTasks.filter(
        (task) => !task.tripId && !taskFinalStatuses.includes(task.status)
      )
    : [];

  const filteredDeliveries = showDeliveries
    ? deliveryTasks.filter(
        (task) => !task.tripId && !taskFinalStatuses.includes(task.status)
      )
    : [];

  // Combine all tasks to display on the map
  const filteredTasks = [
    ...filteredPickups,
    ...filteredDeliveries,
    // When searching, include the assigned tasks that match
    ...(searchedOrderNumber ? assignedTasks : []),
  ];

  // Group tasks by location
  const groupedTasks = filteredTasks.reduce(
    (acc, task) => {
      if (!task.address?.location) return acc;
      const locationKey = `${task.address.location.lat},${task.address.location.lng}`;
      if (!acc[locationKey]) {
        acc[locationKey] = [];
      }
      acc[locationKey].push(task);
      return acc;
    },
    {} as Record<string, Task[]>
  );

  // Create marker data
  const markers = Object.entries(groupedTasks)
    .filter(([_, tasksGroup]) => tasksGroup.length > 0)
    .map(([locationKey, tasksGroup]) => {
      const [lat, lng] = locationKey.split(",").map(Number);

      // Check if any task in the group is related to the searched order number
      const isHighlighted = searchedOrderNumber
        ? tasksGroup.some(taskMatchesSearch)
        : false;

      return {
        id: locationKey,
        position: { lat, lng },
        tasks: tasksGroup,
        isHighlighted,
      };
    });

  // If order search is active, sort highlighted markers to appear on top
  const sortedMarkers = searchedOrderNumber
    ? [...markers].sort(
        (a, b) => (b.isHighlighted ? 1 : 0) - (a.isHighlighted ? 1 : 0)
      )
    : markers;

  // Render markers
  return (
    <>
      {sortedMarkers.map((marker) => (
        <AdvancedMarker
          key={marker.id}
          position={marker.position}
          zIndex={marker.isHighlighted ? 1000 : 1}
        >
          <div className="relative group cursor-pointer hover:scale-110 transition-transform duration-200">
            <TaskGroupMarkerComponent
              tasks={marker.tasks}
              zoomLevel={zoomLevel}
              isHighlighted={marker.isHighlighted}
            />
          </div>
        </AdvancedMarker>
      ))}
    </>
  );
}
